import styled from '@emotion/styled/macro'
import { keyframes } from '@emotion/react'
import * as React from 'react'
import type { ReactNode } from 'react'

import { Icon } from '~/components/atoms/Icon'
import { OutlineButton } from '~/components/atoms/OutlineButton'

export interface ButtonsProps {
  className?: string

  state: 'next-only' | 'both' | 'cart-only'

  disabled?: boolean
  cartDisabled?: boolean

  nextButtonLabel: ReactNode
  cartButtonLabel: ReactNode

  onClickNextButton(): void
  onClickCartButton(): void
}

export const Buttons = ({
  state,
  disabled,
  cartDisabled,
  nextButtonLabel,
  cartButtonLabel,
  onClickCartButton,
  onClickNextButton,
  ...rest
}: ButtonsProps) => {
  const isCartBlockedGlobally = !!disabled
  const isCartDisabledByBusiness = !!cartDisabled
  const isCartDisabled = isCartBlockedGlobally || isCartDisabledByBusiness

  return (
    <Container {...rest} data-state={state}>
      {state !== 'cart-only' && (
        <NextButton
          colorTheme="teal"
          disabled={disabled}
          onClick={() => onClickNextButton()}
        >
          <ButtonLabel>{nextButtonLabel}</ButtonLabel>
        </NextButton>
      )}
      {state !== 'next-only' && (
        <CartButton
          colorTheme="red"
          disabled={isCartBlockedGlobally}
          aria-disabled={!isCartBlockedGlobally && isCartDisabledByBusiness}
          onClick={() => onClickCartButton()}
        >
          <ButtonBody data-compact={state === 'both'}>
            <ButtonIcon data-animate={state === 'both'}>
              <Icon type="cart-in" />
            </ButtonIcon>
            <ButtonLabel>{cartButtonLabel}</ButtonLabel>
          </ButtonBody>
        </CartButton>
      )}
    </Container>
  )
}

const Container = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 1;

  display: grid;
  grid-template-columns: minmax(0, auto) max-content;
  grid-template-areas: 'next cart';
  align-items: stretch;
  gap: var(--gutter-small);

  padding: var(--gutter-medium);
  background: var(--white);
  border-top: 1px solid var(--gray-200);

  &[data-state='next-only'] {
    gap: 0;
    grid-template-columns: 100% 0;
  }

  &[data-state='cart-only'] {
    gap: 0;
    grid-template-columns: 0 100%;
  }
`

const NextButton = styled(OutlineButton)`
  grid-area: next;
`

const CartButton = styled(OutlineButton)`
  min-width: 44px;
  grid-area: cart;
`

const ButtonBody = styled.span`
  display: inline-flex;
  gap: var(--gutter-small);
  align-items: center;

  &[data-compact='true'] {
    gap: 0;
  }
`

const shake = keyframes`
  0% {
    transform: rotate(-10deg);
  }

  20% {
    transform: rotate(8deg);
  }

  40% {
    transform: rotate(-6deg);
  }

  60% {
    transform: rotate(4deg);
  }

  80% {
    transform: rotate(-2deg);
  }

  100% {
    transform: rotate(0deg);
  }
`

const ButtonIcon = styled.span`
  display: inline-grid;
  place-items: center;
  font-size: 24px;

  @media not (prefers-reduced-motion: reduce) {
    &[data-animate='true'] {
      animation: 0.3s 0.2s ease-in ${shake};
    }
  }
`

const ButtonLabel = styled.span`
  letter-spacing: 0.1em;
  font-size: 12px;
  line-height: 2;

  user-select: none;
  white-space: nowrap;

  [data-compact='true'] > & {
    display: none;
  }
`
